// The async half of the trip date chain. TripGroups.mjs owns the rule -- a
// leg's date plus its `days` is the next leg's date -- and is pure; this module
// is the only place that reads a group's legs from the database and writes the
// dates a cascade produced. Pages call these two functions; nothing else in the
// app should be fetching sibling legs to re-date them.

import * as Database from './Database.mjs';
import { saveQueue } from './SaveQueue.mjs';
import { legLane, LEG_LANE_PREFIX } from './saveLanes.js';
import { settledLegCascades, legCascadesPending } from './legCascades.mjs';

// The save-queue lane a trip leg's edits run on. Must match the `recordKey`
// Trip.jsx builds, or a wait here would watch a lane nobody writes to -- which
// is why every lane key now has one definition, in saveLanes.js. Re-exported
// here because this is where the callers already look for it.
export { legLane };

// The serialized cascade chain -- `runLegCascade` to queue one, and the two
// readers a page waits on before trusting what it just fetched. It lives in its
// own leaf module (setupTests.js has to reset it without pulling in Database),
// and is re-exported here because this is where the callers already look.
export { runLegCascade } from './legCascades.mjs';
export { settledLegCascades, legCascadesPending };

// Every leg lane with a write still going -- the lanes a reader has to wait out
// when it cannot NAME them.
//
// The named form (map the legs you were handed to their lanes) only answers for
// a page that was handed legs. TripGroup is reached one click up the breadcrumb
// from the leg page, and a breadcrumb carries no router state at all: it has no
// leg ids to build lanes from, and the cascade that will re-date the siblings
// has not been queued yet -- runLegCascade fires from the save's onSaved, which
// is still a round trip away. So `legCascadesPending()` is false, every named
// lane list is empty, and the page reads the pre-save dates with nothing to
// correct them later. That was the "changed Days from 1 to 2, and the next leg
// still starts the same day" bug.
//
// Same shape as the place family's prefix wait (Places.jsx, usePlaceRecords),
// and the same error-lane exclusion comes free from pendingKeys: a parked lane
// will never drain on its own, so waiting on it would hang.
export function pendingLegLanes() {
  return saveQueue.pendingKeys().filter((key) => key.startsWith(LEG_LANE_PREFIX));
}

// Every leg of a group, in date order, safe to cascade from.
//
// A cascade re-dates legs RELATIVE to what's stored, so reading a leg whose own
// edit is still in flight would chain off the pre-save date and write the wrong
// answer everywhere after it. The lanes can't be checked before the ids are
// known, hence fetch, check, and refetch -- the second read only happens when
// something is genuinely still pending, which is rare (the caller is usually a
// page whose own save has just landed).
//
// Deliberately NOT abortable, unlike every other lane wait in the app: this is
// the read half of a write that has to happen, so abandoning it on unmount
// abandons the cascade. The wait is bounded anyway -- retries are capped and a
// parked lane doesn't count as pending -- and a lane held open by someone still
// typing is a lane we genuinely must not cascade off.
export async function loadGroupLegs(itemId, groupKey) {
  const legs = await Database.listTripsForGroupKey(itemId, groupKey);
  const lanes = legs.map((leg) => legLane(leg.id));
  if (!lanes.some((lane) => saveQueue.isPending(lane))) return legs;

  await saveQueue.settledAll(lanes);
  return Database.listTripsForGroupKey(itemId, groupKey);
}

// Trips read through `read`, safe to paint as current.
//
// Same hazard as loadGroupLegs, different consumer: these callers are READ-only
// pages, so a leg edited on its own (autosaving) detail page can be handed back
// to one inside the 1.5s debounce -- or while the write is in flight -- and the
// row we just read holds pre-save values with nothing to correct it later. The
// lanes can't be checked before the ids are known, hence fetch, check, refetch;
// the second read only happens when a leg is genuinely still pending, which is
// rare. (See README.md § "Autosave (no Save buttons)" and .claude/rules/autosave.md.)
//
// The same wait covers a cascade: the leg the user edited is only ONE of the
// rows these pages show, so a re-dated sibling is stale for exactly as long as
// the cascade takes -- and unlike a save queue job, nothing else reports it.
// Checked after the fetch so it covers cascades queued while the fetch was out,
// and after the LANES rather than before, because a cascade is queued from a
// save's onSaved -- which the queue runs before it notifies the subscribers a
// lane wait resolves on.
//
// Pass the `signal` of an AbortController you abort on unmount -- an
// error-parked lane would otherwise hold the subscription for the page's life.
async function freshTrips(read, { signal } = {}) {
  const trips = await read();
  const lanes = trips.map((trip) => legLane(trip.id)).filter((lane) => saveQueue.isPending(lane));
  if (lanes.length === 0 && !legCascadesPending()) return trips;

  await saveQueue.settledAll(lanes, { signal });
  if (signal?.aborted) return trips;
  await settledLegCascades();
  if (signal?.aborted) return trips;
  return read();
}

// An item's upcoming trips -- the 30-day-lookback window the item page's
// Upcoming Trips card shows.
export function loadUpcomingTripsFresh(itemId, options) {
  return freshTrips(() => Database.listUpcomingTripsForItem(itemId), options);
}

// EVERY leg the item has, paged to the end.
//
// The calendar draws a trip as one bar spanning its whole date range, so a
// partially paginated read wouldn't just show fewer trips -- it would draw the
// ones it did show too SHORT, with no sign anything was missing. That is why
// this page doesn't reuse the trips list's infinite scroll.
export function loadAllTripsFresh(itemId, options) {
  return freshTrips(() => Database.listTripsForItem(itemId), options);
}

// Persist the `updates` a cascade produced ({ id, date } each).
//
// Only `date` goes out. The reserved `fields` blob holds the map route, and an
// update that omitted it would be fine but one that carried a stale copy would
// not -- these legs aren't the ones being edited, so we never send anything we
// didn't just compute.
//
// Written straight through Database.updateTrip rather than scheduled on each
// leg's save-queue lane: a hand-built job carries its own payload, so a form
// that later writes the same record would drop it as superseded and the date
// would silently never land (see README.md § "Autosave (no Save buttons)"). No form owns these
// lanes while a cascade runs, so there's nothing to serialize against.
//
// allSettled, not all: a cascade is several independent writes, and reporting
// the first rejection while the rest are still in flight would leave a caller
// trying to repair a database that's still moving. Resolves to the saved rows
// (in `updates` order) or throws the first rejection once everything is quiet.
export async function persistLegDates(updates, itemId) {
  if (!updates || updates.length === 0) return [];

  const results = await Promise.allSettled(
    updates.map((u) => Database.updateTrip({ id: u.id, itemId, date: u.date }))
  );
  const failed = results.find((r) => r.status === 'rejected');
  if (failed) throw failed.reason;
  return results.map((r) => r.value);
}
